import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ChartCard from './ChartCard';
import { HEATMAP_EMPTY, HEATMAP_RAMP } from './chartTheme';
import { HeatmapCell } from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface BusyHoursHeatmapProps {
  hours: number[];
  cells: HeatmapCell[];
}

const WEEKDAY_SHORT_KEYS = [
  COMMON_TRANSLATE_KEYS.MONDAY_SHORT,
  COMMON_TRANSLATE_KEYS.TUESDAY_SHORT,
  COMMON_TRANSLATE_KEYS.WEDNESDAY_SHORT,
  COMMON_TRANSLATE_KEYS.THURSDAY_SHORT,
  COMMON_TRANSLATE_KEYS.FRIDAY_SHORT,
  COMMON_TRANSLATE_KEYS.SATURDAY_SHORT,
  COMMON_TRANSLATE_KEYS.SUNDAY_SHORT,
];

const WEEKDAY_KEYS = [
  COMMON_TRANSLATE_KEYS.MONDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.TUESDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.WEDNESDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.THURSDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.FRIDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.SATURDAY_NORMAL,
  COMMON_TRANSLATE_KEYS.SUNDAY_NORMAL,
];

const formatHour = (hour: number) => `${String(hour % 24).padStart(2, '0')}:00`;

const BusyHoursHeatmap = ({ hours, cells }: BusyHoursHeatmapProps) => {
  const { t } = useTranslation();

  const maxCount = useMemo(
    () => cells.reduce((max, cell) => Math.max(max, cell.count), 0),
    [cells]
  );

  const getColor = (count: number) => {
    if (count === 0 || maxCount === 0) {
      return HEATMAP_EMPTY;
    }

    const step = Math.ceil((count / maxCount) * HEATMAP_RAMP.length) - 1;

    return HEATMAP_RAMP[Math.max(0, step)];
  };

  const gridTemplateColumns = `2.5rem repeat(${hours.length}, minmax(1.75rem, 1fr))`;

  return (
    <ChartCard
      title={t(COMMON_TRANSLATE_KEYS.STATS_BUSY_HOURS)}
      subtitle={t(COMMON_TRANSLATE_KEYS.STATS_BUSY_HOURS_HINT)}
      legend={
        <div className="flex items-center gap-2 text-sm text-textGray">
          <span>{t(COMMON_TRANSLATE_KEYS.STATS_LESS)}</span>
          {[HEATMAP_EMPTY, ...HEATMAP_RAMP].map((color) => (
            <span
              key={color}
              className="inline-block w-4 h-4 rounded-sm"
              style={{ backgroundColor: color }}
            />
          ))}
          <span>{t(COMMON_TRANSLATE_KEYS.STATS_MORE)}</span>
          {maxCount > 0 && (
            <span className="ml-1 text-mainWhite tabular-nums">
              (max {maxCount})
            </span>
          )}
        </div>
      }
    >
      <div className="overflow-x-auto">
        <div
          className="grid gap-[2px] min-w-max text-xs"
          style={{ gridTemplateColumns }}
        >
          <span />
          {hours.map((hour) => (
            <span
              key={hour}
              className="text-textGray text-center tabular-nums pb-1"
            >
              {hour % 2 === 0 ? String(hour % 24).padStart(2, '0') : ''}
            </span>
          ))}
          {WEEKDAY_SHORT_KEYS.map((dayKey, weekday) => (
            <React.Fragment key={dayKey}>
              <span className="text-textGray flex items-center">
                {t(dayKey)}
              </span>
              {hours.map((hour) => {
                const count =
                  cells.find((c) => c.weekday === weekday && c.hour === hour)
                    ?.count || 0;

                return (
                  <span
                    key={hour}
                    className="h-7 rounded-[4px] hover:ring-2 hover:ring-mainWhite"
                    style={{ backgroundColor: getColor(count) }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={t(
                      COMMON_TRANSLATE_KEYS.STATS_BUSY_HOURS_CELL,
                      {
                        day: t(WEEKDAY_KEYS[weekday]),
                        from: formatHour(hour),
                        to: formatHour(hour + 1),
                        count,
                      }
                    )}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </ChartCard>
  );
};

export default BusyHoursHeatmap;
